import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { clearErrorAction, getAllComplainAction } from '../redux/actions/complainAction'
import swal from 'sweetalert';
import EditIcon from '@material-ui/icons/Edit'
import Loader from '../layout/loader'
import Sidebar from './Sidebar'
import './allComplainByAdmin.css'

function AllComplainByAdmin() {

    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const { loading, error, complains } = useSelector((state) => state.allComplain)

    useEffect(() => {
        if (error) {
            swal({
                title: "Validation Error",
                text: error,
                icon: "error",
            })
            dispatch(clearErrorAction())

        }

        dispatch(getAllComplainAction())

    }, [dispatch, error])

    const complainList = useMemo(() => Array.isArray(complains) ? complains : [], [complains]);

    const getStatus = (complain) => (complain.complainStatus && complain.complainStatus.status) || 'Complain in Process';

    const getStatusTone = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        if (normalizedStatus.includes('solved')) {
            return 'solved';
        }

        if (normalizedStatus.includes('process')) {
            return 'process';
        }

        return 'open';
    }

    const statusOptions = useMemo(() => {
        const statuses = new Set(complainList.map((complain) => getStatus(complain)).filter(Boolean));
        return ['All', ...Array.from(statuses)];
    }, [complainList]);

    const filteredComplains = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return complainList.filter((complain) => {
            const complainStatus = getStatus(complain);
            const statusMatch = statusFilter === 'All' || complainStatus === statusFilter;
            const searchableText = [
                complain._id,
                complain.userName,
                complain.userEmail,
                complain.userNumber,
                complain.complainType,
                complain.complainMsg,
                complainStatus,
            ].join(' ').toLowerCase();

            return statusMatch && (!normalizedSearch || searchableText.includes(normalizedSearch));
        });
    }, [complainList, searchTerm, statusFilter]);

    const summary = useMemo(() => {
        const solved = complainList.filter((complain) => getStatus(complain) === 'Solved').length;
        const active = complainList.length - solved;
        const recent = complainList.filter((complain) => {
            const createdDate = complain.complainStatus && complain.complainStatus.createdAt;
            if (!createdDate) {
                return false;
            }

            const daysSinceCreated = (Date.now() - new Date(createdDate).getTime()) / (1000 * 60 * 60 * 24);
            return daysSinceCreated <= 7;
        }).length;

        return {
            total: complainList.length,
            active,
            solved,
            recent,
        };
    }, [complainList]);

    return (
        <Fragment>
            {loading ? <Loader /> : <Fragment>
                <div className="dashboard-container">
                    <Sidebar />
                    <div className="productListContainer admin-complaints-page">
                        <div className='admin-list-hero admin-complaints-hero'>
                            <div>
                                <span className="admin-hero-kicker">Support operations</span>
                                <h1>Complaints</h1>
                                <p>Open customer complaint records, track active cases, and resolve customer issues.</p>
                            </div>
                            <div className="admin-complaints-hero-stats">
                                <div>
                                    <span>Total complaints</span>
                                    <strong>{summary.total}</strong>
                                </div>
                                <div>
                                    <span>Active cases</span>
                                    <strong>{summary.active}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="admin-complaints-toolbar">
                            <input
                                type="search"
                                placeholder="Search complaint id, customer, email, type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                {statusOptions.map((status) => (
                                    <option value={status} key={status}>{status}</option>
                                ))}
                            </select>
                            <div className="admin-complaints-summary-card">
                                <span>Solved</span>
                                <strong>{summary.solved}</strong>
                            </div>
                            <div className="admin-complaints-summary-card">
                                <span>Last 7 days</span>
                                <strong>{summary.recent}</strong>
                            </div>
                        </div>

                        <div className="admin-complaints-table-card">
                            <div className="admin-complaints-table-scroll">
                                <table className="admin-complaints-table">
                                    <thead>
                                        <tr>
                                            <th>Complaint</th>
                                            <th>Customer</th>
                                            <th>Issue</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredComplains.length > 0 ? filteredComplains.map((complain) => {
                                            const complainStatus = getStatus(complain);
                                            const createdDate = complain.complainStatus && complain.complainStatus.createdAt;

                                            return (
                                                <tr key={complain._id}>
                                                    <td data-label="Complaint">
                                                        <div className="admin-complaint-id">{complain._id}</div>
                                                        <span>{complain.complainType || 'General complaint'}</span>
                                                    </td>
                                                    <td data-label="Customer">
                                                        <div className="admin-complaint-customer">{complain.userName || 'Customer'}</div>
                                                        <span>{complain.userEmail || 'No email'}</span>
                                                        <span>{complain.userNumber || 'No phone'}</span>
                                                    </td>
                                                    <td data-label="Issue">
                                                        <div className="admin-complaint-message">{complain.complainMsg || 'No message'}</div>
                                                    </td>
                                                    <td data-label="Status">
                                                        <span className={`admin-complaint-chip complaint-${getStatusTone(complainStatus)}`}>
                                                            {complainStatus}
                                                        </span>
                                                    </td>
                                                    <td data-label="Date">
                                                        <strong className="admin-complaint-date">
                                                            {createdDate ? new Date(createdDate).toLocaleDateString('en-IN', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }) : 'No date'}
                                                        </strong>
                                                        {complain.solveAt && complainStatus === 'Solved' && (
                                                            <span>Solved {new Date(complain.solveAt).toLocaleDateString('en-IN', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Action">
                                                        <Link className="admin-complaint-action-button" to={`/Admin/Singel/complain/${complain._id}`}>
                                                            <EditIcon /> Review
                                                        </Link>
                                                    </td>
                                                </tr>
                                            )
                                        }) : (
                                            <tr>
                                                <td colSpan="6">
                                                    <div className="admin-complaints-empty">
                                                        <strong>No complaints found</strong>
                                                        <span>Try changing the search or status filter.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>}

        </Fragment>
    )
}

export default AllComplainByAdmin
